import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Product } from '../../../../Model/Catalog/Product';
import { ModelService } from '../../../../Services/model.service';
import { FeatureSystem } from '../../../../Model/Catalog/FeatureModels/FeatureSystem';
import { ViewService } from '../../../../Services/view.service';
import { DxAccordionComponent } from 'devextreme-angular';
import { Message } from '../../../../Model/System/Message';
import { custom } from 'devextreme/ui/dialog';
import { TemplateService } from '../../../../Services/template.service';
import { UserManagementService } from '../../../../Services/userManagment.service';
import { CatalogService } from '../../../../Services/CatalogManagement/catalog.service';
import { ValidationService } from '../../../../Services/Validation/validation.service';
import { ValidFeatureService } from '../../../../Services/validFeature.service';
import { LoginService } from '../../../../Services/login.service';
import { lastValueFrom } from 'rxjs';
import { ProductService } from '../../../../Services/CatalogManagement/product.service';
import { ClickEvent } from 'devextreme/ui/button';
import { ItemTitleClickEvent } from 'devextreme/ui/accordion';
import { ClassificationClass } from '../../../../Model/classification/ClassificationClass';

@Component({
    selector: 'featuresystems',
    templateUrl: 'featuresystems.component.html',
    styleUrls: ['featuresystems.component.css', '../../template.css'],
    standalone: false
})
export class EditFeatureSystemList implements OnInit {
    @ViewChild(DxAccordionComponent, { static: true }) accordion: DxAccordionComponent;
    @Input() templateView: boolean = false;
    @Input() hideDefaultSystems: boolean = false;

    _featureSystems: FeatureSystem[];
    _filteredFeatureSystems: FeatureSystem[];

    get featureSystems(): FeatureSystem[] {
        return this._filteredFeatureSystems;
    }

    @Input()
    set featureSystems(value: FeatureSystem[]) {
        this._featureSystems = value;
        this._updateFilteredFeatureSystems();
    }

    private _updateFilteredFeatureSystems() {
        this._filteredFeatureSystems = this._featureSystems?.filter(fs => !this.excludedSystemNames.includes(fs.referenceFeatureSystemName)) ?? [];
    }

    @Input() hintsAktiv = false;
    @Input() showToolTips = false;
    @Input() showProToolTips = false;
    @Input() showPlaceHolders = true;
    @Input() showLinkButtons: boolean = false;

    @Input() displayMode: 'edit' | 'import' | 'export';

    @Input() model: Product = null;
    @Input() selectedElements: [''];
    @Output() elementSelected = new EventEmitter<string>();

    @Input() showDragHandle: boolean = false;
    @Input() dragFilter: string = 'noDrag';

    @Output() excelElementDropped = new EventEmitter<any>();
    @Output() elementDeleted = new EventEmitter<any>();

    @Output() onValueChanged = new EventEmitter<any>();
    @Output() addEcommerceFeatures = new EventEmitter<any>();
    @Output() addClassificationFeatures = new EventEmitter<ClassificationClass>();

    classificationPopupVisible = false;
    updateLock: Boolean = false;
    updateFrontendLinkedFields: boolean = false;

    defaultFeatureSystems = ['udf_NMTECHNICALDETAILS-1.0'];

    excludedSystemNames = [
      'udf_NMMARKETINGCLAIM-1.0',
      'udf_NMBENEFITS-1.0',
      'udf_NMTOPFEATURES-1.0'
    ];

    constructor(
        public modelService: ModelService,
        public templateService: TemplateService,
        public translate: TranslateService,
        public validationService: ValidationService,
        public userManagement: UserManagementService,
        public catalogService: CatalogService,
        public validFeatureService: ValidFeatureService,
        public loginService: LoginService,
        private productService: ProductService
    ) {
    }

    async ngOnInit() {
        await this.refreshFeatureSystemSource();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['featureSystems'] || changes['[hideDefaultSystems]']) {
            if (!this.hideDefaultSystems) {
                this.addDefaultSystems();
            }
        }
    }

    async refreshFeatureSystemSource() {
      const existingNames = new Set(this.featureSystemNameSource);
  
      let result = await lastValueFrom(this.validFeatureService.getSystems(this.loginService.currentCustomer.id));
  
      result.forEach((system) => {
        if (!existingNames.has(system.name)) {
          existingNames.add(system.name);
          this.featureSystemNameSource.push(system.name);
        }
      });    
  
      this.excludedSystemNames.forEach((excludedName) => {
        const indexOfNameSource = this.featureSystemNameSource.indexOf(excludedName);
        if (indexOfNameSource !== -1) {
          this.featureSystemNameSource.splice(indexOfNameSource, 1);
        }
  
      });
  
      this.featureSystemNameSource.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
    }
  
    async addFeatureSystemButtonClicked() {
        const featureSystem = new FeatureSystem();
        featureSystem.editMode = true;
        featureSystem.validFeatureSystemId = await this.validFeatureService.getValidIdForSystemName(
            '',
            this.loginService.currentCustomer.id
        );
        featureSystem.order = (this.featureSystems.reduce((maxOrder, fs) => Math.max(maxOrder, fs.order ?? 0), 0) ?? 0) + 1;

        this._featureSystems.push(featureSystem);
        this._updateFilteredFeatureSystems();
        if (this.displayMode == 'edit') this.update();
    }

    async setFeatureValidId(featureSystem: FeatureSystem) {
        var result = await lastValueFrom(
            this.validFeatureService.setValidIds(this.loginService.currentCustomer.id, featureSystem)
        );
        let fs = Object.assign(new FeatureSystem(), result);
        featureSystem.validFeatureSystemId = fs.validFeatureSystemId;
        featureSystem.features = fs.features;
        featureSystem.updateLinkedFields = true;
        this.updateFrontendLinkedFields = true;
    }

    async updateFeatureSystemNameAndUpdate(event, featureSystem: FeatureSystem) {
        event.previousValue = featureSystem.validFeatureSystemId;
        await this.setFeatureValidId(featureSystem);
        event.value = featureSystem.validFeatureSystemId;
        this.update(event, featureSystem, 'REFERENCE_FEATURE_SYSTEM_NAME', featureSystem.validFeatureSystemId);
    }

    update(event = null, data?, field?: string, system: string = '-1') {
        if (event && data && !event.createTemplateItem) {
            event.dField = field;
            if (system != '-1' || !this.templateView) {
                event.dSystem = system;
            } else {
                event.dSystem = data.order.toString();
            }
            event.dElement = '-1';
            event.createTemplateItem = true;
        }

        if (data == undefined || this.featureSystemIsValid(data)) {
            this.onValueChanged.emit(event);
        }
    }

    onAccordionItemClick(event: ItemTitleClickEvent<FeatureSystem>) {
        if (event.itemData.editMode) {
            console.log('editclick');
            event.event.stopImmediatePropagation();
        }
    }

    onDeleteClick(event: ClickEvent, fs: FeatureSystem) {
        event.event.stopPropagation();
        let product = this.model ?? this.modelService.catalogService.product;
        let system = fs.validFeatureSystemId ? fs.validFeatureSystemId : fs.order.toString();
        if (product != null && product.isChild && this.hasFeatureSystemFieldLinked(system)) {
            this.modelService.systemService.notify(
                new Message(this.translate.instant('DeleteLinkedFieldNotAllowed'), 'info'),
                3000
            );
            return;
        }

        custom({
            title: this.translate.instant('Wirklich löschen?'),
            messageHtml: this.translate.instant('Wollen Sie das FeatureSystem wirlich löschen'),
            buttons: [
                {
                    text: this.translate.instant('Ja'),
                    onClick: (e) => {
                        this.delete(event, fs);
                        return false;
                    }
                },
                {
                    text: this.translate.instant('Nein'),
                    onClick: (e) => {
                        return true;
                    }
                }
            ]
        }).show();
    }

    deleteFeature(event) {
        this.elementDeleted.emit(event);
    }

    delete(event: ClickEvent, fs: FeatureSystem) {
        let product = this.model ?? this.modelService.catalogService.product;
        let system = fs.validFeatureSystemId ? fs.validFeatureSystemId : fs.order.toString();

        if (product) {
            let indexToRemove = product.featureSystems.findIndex((x) => x == fs);
            product.featureSystems.splice(indexToRemove, 1);
        } else {
            this._featureSystems.splice(this._featureSystems.findIndex((x) => x == fs), 1);
        }
        this._updateFilteredFeatureSystems();

        for (let featureSystemField of this.templateService.allFeatureSystemFields) {
            if (product != null && product.isFieldLinked(featureSystemField, system, '-1')) {
                product.toggleLinkedField(featureSystemField, system, '-1');
            }
        }
        this.onValueChanged.emit(event);
        this.elementDeleted.emit({
            dFields: this.templateService.allFeatureSystemFields,
            dDetailFields: this.templateService.allFeatureFields,
            dSystem: system,
            dElement: '-1'
        });
    }

    public onRowRemoving(e) {
        let product = this.modelService.catalogService.product;
        let system;
        if (e.data.validFeatureSystemId) {
            system = e.data.validFeatureSystemId;
        } else {
            system = e.data.order.toString();
        }

        if (product != null && product.isChild && this.hasFeatureSystemFieldLinked(system)) {
            this.modelService.systemService.notify(
                new Message(this.translate.instant('DeleteLinkedFieldNotAllowed'), 'info'),
                3000
            );
            e.cancel = true;
            return;
        }

        let myDialog = custom({
            title: this.translate.instant('Wirklich löschen?'),
            messageHtml: this.translate.instant('Wollen Sie das FeatureSystem wirlich löschen'),
            buttons: [
                {
                    text: this.translate.instant('Ja'),
                    onClick: (e) => {
                        return false;
                    }
                },
                {
                    text: this.translate.instant('Nein'),
                    onClick: (e) => {
                        return true;
                    }
                }
            ]
        });

        e.cancel = myDialog.show();
    }

    async onContentReady() {
        this.updateLock = false;
        if (this.updateFrontendLinkedFields) {
            this.updateFrontendLinkedFields = false;
            this.model.linkedFields = await lastValueFrom(
                this.productService.getLinkedFields(this.model.id, this.loginService.currentCustomer.id)
            );
        }
    }

    async addDefaultSystems() {
        let missingFeatureSystemsPromises = this.defaultFeatureSystems
            .filter((defaultfs) => !this.featureSystems.some((x) => x.referenceFeatureSystemName === defaultfs))
            .map(async (defaultfs) => {
                let validFeatureSystemId = await this.validFeatureService.getValidIdForSystemName(
                    defaultfs,
                    this.loginService.currentCustomer.id
                );
                return { defaultfs, validFeatureSystemId };
            });

        const missingFeatureSystems = await Promise.all(missingFeatureSystemsPromises);

        missingFeatureSystems.forEach(({ defaultfs, validFeatureSystemId }) => {
            let featureSystem = new FeatureSystem();
            featureSystem.referenceFeatureSystemName = defaultfs;
            featureSystem.validFeatureSystemId = validFeatureSystemId;
            featureSystem.order =
                (this.featureSystems.reduce((maxOrder, fs) => Math.max(maxOrder, fs.order ?? 0), 0) ?? 0) + 1;

            this._featureSystems.push(featureSystem);
        });
        this._updateFilteredFeatureSystems();
    }

    toggleFeatureSystemEditMode(e, fs: FeatureSystem) {
        e.event.stopPropagation();
        fs.editMode = !fs.editMode;
        this.accordion.instance.repaint();
    }

    isVirtualCatalog(): boolean {
        return this.catalogService?.catalog?.isVirtual;
    }

    translateMessage(msg: string) {
        return this.translate.instant(msg);
    }

    addClassificationButtonVisible(): boolean {
        return true;
    }

    addFeatureSystemButtonVisible(): boolean {
        return true;
    }

    addClassificationButtonClicked() {
        this.classificationPopupVisible = true;
    }

    closeClassificationPopup() {
        this.classificationPopupVisible = false;
    }

    emitAddClassificationFeatures(event: ClassificationClass) {
        this.addClassificationFeatures.emit(event);
    }

    emitAddEcommerceFeatures() {
        this.addEcommerceFeatures.emit();
    }

    get featureSystemNameSource(): string[] {
        return ViewService.dropDownFeatureSystemNames;
    }

    get featureSystemEditable(): boolean {
        return true;
    }

    get profiModeEnabled(): boolean {
        return this.modelService.loginService.currentUser.featureProfiMode;
    }

    get showValidationSummary(): boolean {
        return this.modelService.loginService.currentCustomer.automaticValidation && !this.validationService.validationResult.productFeaturesValid;
    }

    get validationMessages(): string[] {
        return this.validationService.validationResult.featureMessages;
    }

    get templateSelectionEnabled(): boolean {
        return this.displayMode === 'export' || this.displayMode === 'import';
    }

    templateSelect(identifier: string, orderString: string) {
        this.elementSelected.emit(`${identifier}_${orderString}`);
    }

    templateChildSelected(key: string) {
        this.elementSelected.emit(key);
    }

    excelElementDrop(e) {
        this.excelElementDropped.emit(e);
    }

    excelFeatureSystemNameDroppedError() {
        if (this.displayMode != 'export') {
            this.modelService.systemService.notify(
                new Message(this.translate.instant('FeatureSystemNameDroppedError'), 'error'),
                3000
            );
        }
    }

    public hasFeatureSystemFieldLinked(system: string = '-1'): boolean {
        let product = this.modelService.catalogService.product;
        for (let featureSystemField of this.templateService.allFeatureSystemFields) {
            if (product != null && product.isFieldLinked(featureSystemField, system, '-1')) {
                return true;
            }
        }
        return false;
    }

    public readOnlyForLinkedFields(field: string, system: string = '-1'): boolean {
        let product = this.modelService.catalogService.product;
        if (product != null && field != null) {
            var isLinked = product.isFieldLinked(field, system, '-1');
            if (isLinked && product.isChild != null && product.isChild) {
                return true;
            }
        }
        return false;
    }

    public showClearButton(field: string, system: string, readOnlyName: string = ''): boolean {
        var result = !this.readOnlyForLinkedFields(field, system);
        if (readOnlyName != '') {
            result = result || this.readOnly(readOnlyName);
        }
        return result && !this.templateView;
    }

    public hasMasterOrChildOrIsTranslated(): boolean {
        let product = this.modelService.catalogService.product;
        if (product != null) {
            return product.hasMasterOrChild();
        }
        return false;
    }

    public isFieldLinked(field: string, system: string = '-1'): boolean {
        let product = this.modelService.catalogService.product;
        if (product != null) {
            return product.isFieldLinked(field, system, '-1');
        }
        return false;
    }

    public toggleLinkedField(event = null, field: string, system: string = '-1') {
        let product = this.modelService.catalogService.product;
        var changed = product.toggleLinkedField(field, system, '-1');
        if (!changed) {
            return;
        }

        var isNowLinked = this.isFieldLinked(field, system);
        event.target.className = product.getClassForLinkedField(isNowLinked);

        if (product.isChild && isNowLinked) {
            event.forceReload = true;
        }

        this.update(event);
    }

    public getInitialClassForLinkedField(field: string, system: string = '-1'): string {
        let product = this.modelService.catalogService.product;
        var isNowLinked = this.isFieldLinked(field, system);
        return product.getClassForLinkedField(isNowLinked);
    }

    canView(name: string): boolean {
        return !this.isVirtualCatalog() && this.userManagement.canView(name);
    }

    readOnly(name: string): boolean {
        return this.isVirtualCatalog() || !this.userManagement.canEdit(name);
    }

    readOnlyTemplateEditor(fs) {
        return this.templateView && !this.featureSystemIsValid(fs);
    }

    featureSystemIsValid(featureSystem: FeatureSystem) {
        return featureSystem.referenceFeatureSystemName != undefined;
    }

    getRelevantSystemKey(featureSystem: FeatureSystem) {
        if (featureSystem.validFeatureSystemId) {
            return featureSystem.validFeatureSystemId;
        }
        return featureSystem.order;
    }

    getSystemDetails(featureSystem: FeatureSystem): string {
        let result = "";
        if (featureSystem) {
            if (featureSystem.referenceFeatureGroupId && featureSystem.referenceFeatureGroupId.length > 0) {
                result = featureSystem.referenceFeatureGroupId;
            }
            if (featureSystem.referenceFeatureGroupName && featureSystem.referenceFeatureGroupName.length > 0) {

                if (result.length > 0) {
                    result = result + " | ";
                }
                result = result + featureSystem.referenceFeatureGroupName;
            }
        }
        return result;
    }

}
